import { z } from 'zod';
/**
 * Modèle d'autorisation (cahier des charges §8).
 *
 * Deux dimensions indépendantes gouvernent chaque accès :
 *  1. la **permission** — ce que l'utilisateur a le droit de faire ;
 *  2. la **portée** (scope) — sur quel périmètre de bureaux il peut le faire.
 *
 * Les deux sont vérifiées séparément côté serveur. Un Area Manager possède par
 * exemple `receipts:read` sur plusieurs bureaux mais ne possède jamais
 * `receipts:write`, ce qu'aucune combinaison de portée ne peut contourner.
 */
export const ROLES = {
    ADMINISTRATOR: 'ADMINISTRATOR',
    AREA_MANAGER: 'AREA_MANAGER',
    BRANCH_LEADER: 'BRANCH_LEADER',
    BRANCH_ASSISTANT: 'BRANCH_ASSISTANT',
    VIEWER: 'VIEWER',
};
export const roleSchema = z.enum([
    ROLES.ADMINISTRATOR,
    ROLES.AREA_MANAGER,
    ROLES.BRANCH_LEADER,
    ROLES.BRANCH_ASSISTANT,
    ROLES.VIEWER,
]);
export const ROLE_LABELS = {
    ADMINISTRATOR: 'Administrateur',
    AREA_MANAGER: 'Area Manager',
    BRANCH_LEADER: 'Branch Leader',
    BRANCH_ASSISTANT: 'Branch Assistant',
    VIEWER: 'Consultation',
};
export const ROLE_DESCRIPTIONS = {
    ADMINISTRATOR: "Accès complet au système, à la sécurité et à la configuration.",
    AREA_MANAGER: 'Consultation et contrôle des bureaux placés sous sa responsabilité.',
    BRANCH_LEADER: "Responsable de l'archivage de son bureau, du lot jusqu'à l'archivage définitif.",
    BRANCH_ASSISTANT: "Opérations d'archivage de son bureau, selon les autorisations accordées.",
    VIEWER: 'Consultation seule, sur tous les bureaux : rechercher et lire les reçus archivés, sans aucune action sur les archives.',
};
/** Permissions granulaires. Le format `domaine:action` est contractuel. */
export const PERMISSIONS = {
    // Organisation
    BRANCHES_READ: 'branches:read',
    BRANCHES_WRITE: 'branches:write',
    // Identités
    USERS_READ: 'users:read',
    USERS_WRITE: 'users:write',
    // Lots de numérisation
    LOTS_READ: 'lots:read',
    LOTS_CREATE: 'lots:create',
    LOTS_SCAN: 'lots:scan',
    LOTS_RUN_OCR: 'lots:run-ocr',
    LOTS_VALIDATE: 'lots:validate',
    LOTS_ARCHIVE: 'lots:archive',
    LOTS_DELETE: 'lots:delete',
    // Documents archivés
    RECEIPTS_READ: 'receipts:read',
    RECEIPTS_DOWNLOAD: 'receipts:download',
    RECEIPTS_PRINT: 'receipts:print',
    RECEIPTS_CORRECT: 'receipts:correct',
    RECEIPTS_DELETE: 'receipts:delete',
    // Exceptions
    DUPLICATES_RESOLVE: 'duplicates:resolve',
    // Pilotage
    DASHBOARD_READ: 'dashboard:read',
    STATISTICS_READ: 'statistics:read',
    // Gouvernance
    AUDIT_READ: 'audit:read',
    SETTINGS_READ: 'settings:read',
    SETTINGS_WRITE: 'settings:write',
    OCR_TEMPLATES_READ: 'ocr-templates:read',
    OCR_TEMPLATES_WRITE: 'ocr-templates:write',
    AI_CONFIGURE: 'ai:configure',
    BACKUPS_MANAGE: 'backups:manage',
};
export const permissionSchema = z.enum(Object.values(PERMISSIONS));
/**
 * Portée d'accès aux bureaux.
 * - `OWN_BRANCH` : uniquement le bureau de rattachement de l'utilisateur.
 * - `ASSIGNED_BRANCHES` : les bureaux explicitement confiés (Area Manager).
 * - `ALL_BRANCHES` : l'ensemble de l'organisation (Administrateur).
 */
export const ACCESS_SCOPES = {
    OWN_BRANCH: 'OWN_BRANCH',
    ASSIGNED_BRANCHES: 'ASSIGNED_BRANCHES',
    ALL_BRANCHES: 'ALL_BRANCHES',
};
export const accessScopeSchema = z.enum([
    ACCESS_SCOPES.OWN_BRANCH,
    ACCESS_SCOPES.ASSIGNED_BRANCHES,
    ACCESS_SCOPES.ALL_BRANCHES,
]);
export const ROLE_SCOPES = {
    ADMINISTRATOR: ACCESS_SCOPES.ALL_BRANCHES,
    AREA_MANAGER: ACCESS_SCOPES.ASSIGNED_BRANCHES,
    BRANCH_LEADER: ACCESS_SCOPES.OWN_BRANCH,
    BRANCH_ASSISTANT: ACCESS_SCOPES.OWN_BRANCH,
    /*
     * Le profil de consultation n'est pas un profil de bureau : il répond à un
     * besoin transversal — retrouver un reçu, d'où qu'il vienne. Le cloisonner
     * par bureau le rendrait inutilisable dès la première recherche portant sur
     * une autre agence. Sa portée est donc universelle en **lecture**, ce qui
     * reste sans danger : il ne détient aucune permission d'écriture, et le §8
     * réserve l'archivage au bureau d'origine indépendamment de la portée.
     */
    VIEWER: ACCESS_SCOPES.ALL_BRANCHES,
};
const CONSULTATION_PERMISSIONS = [
    PERMISSIONS.RECEIPTS_READ,
    PERMISSIONS.RECEIPTS_DOWNLOAD,
    PERMISSIONS.RECEIPTS_PRINT,
    PERMISSIONS.LOTS_READ,
    PERMISSIONS.BRANCHES_READ,
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.STATISTICS_READ,
];
const ARCHIVING_PERMISSIONS = [
    PERMISSIONS.LOTS_CREATE,
    PERMISSIONS.LOTS_SCAN,
    PERMISSIONS.LOTS_RUN_OCR,
    PERMISSIONS.LOTS_ARCHIVE,
    PERMISSIONS.RECEIPTS_CORRECT,
    PERMISSIONS.DUPLICATES_RESOLVE,
    PERMISSIONS.OCR_TEMPLATES_READ,
];
/**
 * Permissions par défaut attachées à chaque rôle.
 *
 * Un utilisateur peut recevoir des permissions supplémentaires à titre
 * individuel (cas du « Branch Assistant selon les autorisations accordées »),
 * mais jamais au-delà de ce que son rôle rend légitime : l'octroi individuel
 * est validé contre `GRANTABLE_PERMISSIONS`.
 */
export const ROLE_PERMISSIONS = {
    ADMINISTRATOR: Object.values(PERMISSIONS),
    AREA_MANAGER: [...CONSULTATION_PERMISSIONS, PERMISSIONS.USERS_READ, PERMISSIONS.AUDIT_READ],
    BRANCH_LEADER: [
        ...CONSULTATION_PERMISSIONS,
        ...ARCHIVING_PERMISSIONS,
        PERMISSIONS.LOTS_VALIDATE,
        PERMISSIONS.USERS_READ,
    ],
    BRANCH_ASSISTANT: [...CONSULTATION_PERMISSIONS, ...ARCHIVING_PERMISSIONS],
    /**
     * Consultation seule.
     *
     * Volontairement réduit à la lecture des reçus : ni téléchargement, ni
     * impression, ni tableau de bord, ni accès aux lots. Le rôle répond à un
     * besoin précis — retrouver et lire un reçu — et tout ce qui dépasse ce
     * besoin doit être accordé nommément, compte par compte, via
     * `GRANTABLE_PERMISSIONS`.
     */
    VIEWER: [PERMISSIONS.RECEIPTS_READ],
};
/**
 * Permissions qu'un administrateur peut accorder individuellement, par rôle.
 * Tout ce qui n'y figure pas est structurellement hors d'atteinte du rôle.
 */
export const GRANTABLE_PERMISSIONS = {
    ADMINISTRATOR: [],
    AREA_MANAGER: [],
    BRANCH_LEADER: [PERMISSIONS.RECEIPTS_DELETE, PERMISSIONS.LOTS_DELETE, PERMISSIONS.AUDIT_READ],
    BRANCH_ASSISTANT: [PERMISSIONS.LOTS_VALIDATE, PERMISSIONS.AUDIT_READ],
    // Un compte de consultation peut recevoir le droit d'emporter un document —
    // décision qui relève de l'administrateur, jamais du défaut.
    VIEWER: [PERMISSIONS.RECEIPTS_DOWNLOAD, PERMISSIONS.RECEIPTS_PRINT],
};
export function permissionsForRole(role, additionalPermissions = []) {
    const grantable = new Set(GRANTABLE_PERMISSIONS[role]);
    const granted = additionalPermissions.filter((permission) => grantable.has(permission));
    return [...new Set([...ROLE_PERMISSIONS[role], ...granted])];
}
export const PERMISSION_LABELS = {
    'branches:read': 'Consulter les bureaux',
    'branches:write': 'Gérer les bureaux',
    'users:read': 'Consulter les utilisateurs',
    'users:write': 'Gérer les utilisateurs',
    'lots:read': 'Consulter les lots',
    'lots:create': 'Créer un lot',
    'lots:scan': 'Numériser',
    'lots:run-ocr': "Lancer l'OCR",
    'lots:validate': 'Valider les exceptions',
    'lots:archive': 'Archiver définitivement',
    'lots:delete': 'Supprimer un lot',
    'receipts:read': 'Consulter les reçus',
    'receipts:download': 'Télécharger',
    'receipts:print': 'Imprimer',
    'receipts:correct': 'Corriger les métadonnées',
    'receipts:delete': 'Supprimer un reçu archivé',
    'duplicates:resolve': 'Résoudre les doublons',
    'dashboard:read': 'Accéder au tableau de bord',
    'statistics:read': 'Consulter les statistiques',
    'audit:read': "Consulter le journal d'audit",
    'settings:read': 'Consulter les paramètres',
    'settings:write': 'Modifier les paramètres',
    'ocr-templates:read': 'Consulter les zones OCR',
    'ocr-templates:write': 'Configurer les zones OCR',
    'ai:configure': "Paramétrer l'intelligence artificielle",
    'backups:manage': 'Gérer les sauvegardes',
};
//# sourceMappingURL=rbac.js.map